/* eslint-disable no-redeclare */
const _ = require('lodash')
const suuid = require('short-uuid')
const aiTools = require('@/lib/ai-tools')
const parser = new DOMParser()
/**
 * @typedef {import('./Game').Game} Game
 */
/**
 * 表示一則訊息,
 * 各個part在content中使用<part key="" type="" >{{text}}</part>表示
 */
export class Message{
  /**
   * @callback EventCallback
   * @param {Game} game
   * @param {Object} data
   */
  /**
   * @callback DisplayCallback
   * @param {Game} game
   * @param {Message} current
   * @param {Message} old
   */
  /**
   * TODO 補上缺少的屬性
   * @typedef {Message|{
   *  onInput: EventCallback,
   *  onEvent: EventCallback,
   *  beforeDisplay: DisplayCallback,
   *  afterDisplay: DisplayCallback
   * }} MessageOptions
   */
  /**
   * @param {Message | MessageOptions} opts
   */
  constructor(opts = {}){
    /** @type {string} */
    this.id = opts.id || suuid.generate()
    /**
     * 表示Message的顯示方式
     * (顯示的樣式不同)
     * "default" 一般訊息
     * "narrator" 旁白的訊息
     * @type {string}
     * */
    this.type = opts.type || 'default'
    /**
     * 表示編輯器是否會顯示ID (只在編輯器有用)
     * @type {boolean}
     */
    this.showId = opts.showId || false
    /**
     * 表示說話的人的名稱,
     * 若為錢字號開頭則需另外套用人名 $XXX,
     * TODO 有時間的話把sender改成用物件表示{id, name}
     * @type {string}
     * */
    this.sender = opts.sender || ''
    /**
     * 每次執行getContentHtml()後將內容暫存至該變數中
     * @property {Object}
     * @name Message#data
     */
    Object.defineProperty(this, '_contentHtml', {
      writable: true,
      enumerable: false,
      value: ''
    })
    /**
     * 每次執行splitContent()後將內容暫存至該變數中
     * @property {Object}
     * @name Message#data
     */
    Object.defineProperty(this, '_textContent', {
      writable: true,
      enumerable: false,
      value: ''
    })
    // use setter
    /** @type {string} */
    this.content = opts.content
    /** @type {string} */
    this.ssml = opts.ssml || null
    /**
     * 配音檔案的連結
     * 若有該內容，則播放時不會播放 tts
     * @type {?Object}
     */
    this.audio = opts.audio || null
    this.resetAudioUrl()
    /** @type {?string} */
    this.hint = opts.hint || null
    /** @type {boolean} */
    this.noTTS = opts.noTTS || false
    /**
     * [string] => 下一則訊息的id
     * @type {string | boolean}
     */
    this.next = opts.next || false
    /**
     * if(!next && autoNext)
     * 下一步的Message會自動選擇"messages"中的下一則Message
     * @type {boolean}
     *
     */
    this.autoNext = opts.autoNext
    if(this.autoNext === undefined && !this.next){
      this.autoNext = true
    }
    /**
     * 返回
     * 之後可能會棄用
     * @type {string | boolean}
     */
    this.back = opts.back || false
    /**
     * closest => 以相似度判斷
     * manual => 選擇選項後會將輸入值送至onInput ( 暫時用不到 )
     * @type {string}
     */
    this.selectMode = opts.selectMode || 'ai'
    /** @type {Selection[]} */
    this.selections = opts.selections || []
    this.selections = this.selections.map((selection)=>{
      return new Selection(selection)
    })
    /**
     * 暫時儲存資料用
     * 不序列化該欄位
     * @property {Object}
     * @name Message#data
     */
    this.data = opts.data || {}

    this._onInput = opts.onInput || doNothing
    this._onEvent = opts.onEvent || doNothing
    this._beforeDisplay = opts.beforeDisplay || doNothing
    this._afterDisplay = opts.afterDisplay || doNothing
  }
  resetAudioUrl(){
    if(this.audio && this.audio.id){
      this.audio.url = `${process.env.VUE_APP_API_ENDPOINT}/wordprison/media/audio/${this.audio.id}`
      // this.audio.url = `https://wordprison.s3-ap-southeast-1.amazonaws.com/medias/audios/${this.audio.id}`
    }
  }
  set content(val = ''){
    if(!this._content){
      Object.defineProperty(this, '_content', {
        writable: true,
        enumerable: false,
        value: ''
      })
    }
    this._content = val.trim().replace(/ +/g, ' ')
    this.resetContentHtml()
    return this._content
  }
  get content(){
    return this._content
  }
  /**
   * 更新_contentHtml
   */
  resetContentHtml(){
    if(!this.content)return ''
    this._contentHtml = this.content.replace(/ +/g, ' ').replace('\n', '<br/>').trim()
    return this._contentHtml
  }
  get contentHtml(){
    return this._contentHtml
  }
  getContentHtml(){
    return this._contentHtml
  }
  getTextContent(){
    return this._textContent
  }
  splitContent(){
    let contentHtml = this.resetContentHtml()
    if(!contentHtml)return ['']
    if(contentHtml[0] !== '<'){
      contentHtml = `<content>${contentHtml}</content>`
    }
    let xmlDoc = parser.parseFromString(contentHtml, 'text/xml')
    let root = xmlDoc.childNodes[0]
    let parseError = xmlDoc.querySelector('parsererror')
    if(parseError){
      console.error(parseError)
      throw new Error(parseError.textContent)
    }
    this._textContent = root.textContent
    return {
      parts: this.splitContentHtml(root),
      root
    }
  }
  /**
   * @param {Node} node from DOMParser
   */
  splitContentHtml(node){
    let allowNodeTypes = ['part', 'br', 'span', 'div']
    let parts = []
    for(let child of node.childNodes){
      if(child.nodeName === '#text'){
        parts = parts.concat(child.textContent.split(''))
      }else if(_.includes(allowNodeTypes, child.nodeName)){
        if(child.nodeName === 'br'){
          // console.log('detected br', parts.length)
          parts.push('<br/>')
        }else{
          console.log('detected element', child.nodeName, parts.length)
          let attrs = child.attributes
          let attrsStr = ''
          for(let attr of attrs){
            attrsStr += `${attr.name}="${attr.value}"`
          }
          parts.push(`<${child.nodeName} ${attrsStr}>`)
          parts = parts.concat(this.splitContentHtml(child))
          parts.push(`</${child.nodeName}>`)
        }
      }else{
        throw new Error('element not allow')
      }
    }
    return parts
  }
  validate(opts = {}){
    if(Array.isArray(this.selections)){
      for(let selection of this.selections){
        // TODO
      }
    }
  }
  /**
   * @param {Game} game
   * @param {Message} current
   * @param {Message} old
   */
  beforeDisplay(game, current, old){
    return this._beforeDisplay(game, current, old)
  }
  /**
   * @param {Game} game
   * @param {Message} current
   */
  afterDisplay(game, current){
    return this._afterDisplay(game, current)
  }
  /**
   * 當用戶講話或打字
   * @param {Game} game
   * @param  {*} data
   */
  async onInput(game, data){
    if(!this.selections || this.selections.length === 0){
      return this._onInput(game, data)
    }else if(this.selections.length > 0){
      if(this.selectMode === 'ai' || this.selectMode === 'closest'){
        // 一般的比較相似度
        let { selection, info } = await this.findClosestSelection(data.query)
        data.closestSelection = selection
        data.info = info
        data.mode = 'closest'
        await this._onInput(game, data)
        // data.closestSelection 可能會在 this._onInput 中被更改
        selection = data.closestSelection ? data.closestSelection : selection
        if(info.similarity < Message.closestThreshold){
          let tips = this.data.tips = this.data.tips || {
            round: 0,
            selection: null, // 會在第 1 round 決定該值，
            contents: [],
            message: '',
            warn: '台詞可能不夠明確，請再說一次',
            completed: false
          }
          tips.round += 1
        }else{
          if(this.data.tips){
            this.data.tips.completed = true
          }
          await this.handleSelectionAction(game, data, selection)
        }
      }else if(this.selectMode === 'advance'){
        this.data.tips = this.data.tips || {
          round: 0,
          selection: null, // 會在round 0決定該值，
          contents: [],
          message: '',
          completed: false
        }
        let status = this.data.tips
        if(status.round === 0){
          let { selection, info } = await this.findClosestSelection(data.query)
          data.closestSelection = selection
          data.info = info
          data.mode = 'advance'
          status.message = '可以說說你的想法'
          status.round += 1
          status.selection = selection
          console.log('round 0', selection, info, status)
        }else if(status.round > 0){
          let { selection, info } = await this.findClosestSelection(
            data.query,
            status.selection.data.subSelections,
            {
              force: true,
              fullResult: true
            }
          )
          // console.log('advance-closest', selectionm , status.selection.subSelections)
          data.closestSelection = selection
          delete info.list
          delete info.map
          data.info = info
          data.mode = 'advance'
          status.round += 1
          if(info.similarity > Message.closestThreshold){
            await this.handleSelectionAction(game, data, selection)
          }else{
            status.message = '不太清楚'
          }
        }
        console.log('msg-advance-debug', status)
      }
    }else{
      console.warn('message no handler')
    }
  }
  async handleSelectionAction(game, data, selection){
    switch(selection.action){
      case Selection.Action.TO:
        var to = selection.data.default
        // 若判斷分數過低，將目標指定為 data.default
        if(data.mode === 'closest'){
          to = selection.data.target
        }else if(data.mode === 'advance'){
          to = selection.data.target
          console.log('advance-', selection)
        }else{
          console.warn(`error : data.mode = ${data.mode}`)
        }
        if(to){
          game.to(to)
        }else{
          // console.warn('selection.data 資料不正確')
          // game.nextMessage()
        }
        break
      case Selection.Action.VAR_ADD:
        var { value, field } = selection.data
        if(field && (typeof value === 'number')){
          if((typeof game.status[field]) === 'number'){
            game.status[field] += value
          }else{
            console.warn(`game.status[${field}] is not number`)
          }
        }else{
          console.warn('selection.data 資料不正確')
        }
        game.nextMessage()
        break
      case Selection.Action.VAR_SET:
        var { value, field } = selection.data
        if(field && (typeof value === 'number')){
          game.status[field] = value
        }else{
          console.warn('selection.data 資料不正確')
        }
        game.nextMessage()
        break
    }
  }
  async findClosestSelection(query, selections = this.selections, opts = {}){
    let list = selections.map((selection)=>{
      return selection.words
    })
    let map = {}
    let counter = 0
    let all = []
    let closestSelection
    let info = {
      similarity: -1,
      query: '',
      ai: false
    }
    // i 為 selection 的 index
    for(let i in list){
      let sublist = list[i]
      for(let text of sublist){
        // 如果說的跟比對的完全一樣則不需送出AI的request
        // if(opts.force) => 一定會送出 request
        if(!opts.force){
          if(text === query){
            closestSelection = selections[i]
            info.similarity = 1
            info.query = text
            break
          }
        }
        map[counter] = i
        all.push(text)
        counter += 1
      }
    }
    if(!closestSelection){
      let result = await aiTools.closest(query, all)
      info.similarity = result.similarity
      info.query = query
      if(opts.fullResult){
        info.list = result.list
        // 可透過 selections[map[<index>]] 反向取回對應的 selection
        info.map = map
      }
      info.ai = true
      closestSelection = selections[map[result.index]]
      if(opts.force){
        for(let i in all){
          if(all[i] === query){
            closestSelection = selections[map[i]]
            info.similarity = 1
          }
        }
      }
    }
    return {
      info,
      selection: closestSelection
    }
  }
  /**
   * @param {Game} game
   * @param  {*} args
   */
  onEvent(game, data){
    return this._onEvent(game, data)
  }
  /**
   * 當用戶直接點選項
   */
  onSelection(){
  }
  toJSON(){
    let obj = Object.assign({}, this)
    obj.content = this.content
    if(obj.audio){
      obj.audio = {
        id: this.audio.id,
        duration: this.audio.duration
      }
    }
    delete obj.data
    delete obj._page
    return obj
  }
  static getRoleName(roleId, extraMap = {}){
    if(extraMap[roleId]){
      return extraMap[roleId]
    }
    if(Message.roleNameMap[roleId]){
      return Message.roleNameMap[roleId]
    }
    return null
  }
  static get closestThreshold(){
    return 0.3
  }
}
Message.roleNameMap = {
  '$narrator': '旁白'
}
/**
 * @typedef {Object} SelectionData
 * @property {?Selection[]} subSelections 進階問答時才會有該選項
 * @property {?String} target action = 'TO'時會有該選項
 * @property {?String} value
 * @property {?String} field
 */
class Selection{
  /**
   * @param {Selection} opts
   */
  constructor(opts = {}){
    /**
     * @type {string}
     */
    this.key = opts.key.trim()
    /**
     * @type {string[]}
     */
    this.alias = opts.alias || []
    /**
     * @type {SelectionData}
     */
    this.data = opts.data || {}
    this.setupSubSelections()
    /**
     * @type {?string}
     */
    this.action = opts.action || Selection.Action.TO
    // 檢查alias是否跟key重複
    this.checkAlias()
  }
  get words(){
    return this.alias.concat([this.key]).filter((str)=>str && str.length > 0)
  }
  checkAlias(){
    // 檢查alias是否跟key重複
    let n = this.alias.indexOf(this.key)
    if(n >= 0){
      this.alias.splice(n, 1)
    }
  }
  addAlias(selection){
    let _selection = selection.trim()
    if(this.key !== _selection){
      this.alias.push(_selection)
    }
  }
  setupSubSelections(){
    if(this.data && this.data.subSelections){
      this.data.subSelections = this.data.subSelections.map((selection)=>{
        return new Selection(selection)
      })
    }
  }
  addSubSelection(selection){

  }
}
Selection.Action = {
  TO: 'to',
  VAR_ADD: 'var-add',
  VAR_SET: 'var-set'
}
function doNothing(){}
Message.Selection = Selection
export default Message
